import { useQuery } from '@zg-rentals/react-query5';
import { useState } from 'react';
import { API, BASE_URL } from '../constants/urls';
import { useDebouncedCallback } from '@zg-rentals/applications-components';
import type { ApiResponseDto } from '@zg-rentals/ts-rental-manager-api-client';

export const MIN_AUTOCOMPLETE_ADDRESS_LENGTH = 7;

export type AddressResponse = {
  city: string;
  name: string;
  propertyId: string;
  state: string;
  street: string;
  zip: string;
};

type AddressesResponse = {
  addresses: Array<AddressResponse>;
};

export async function getAddressAutocomplete(partialAddress: string): Promise<ApiResponseDto<AddressesResponse>> {
  const requestUrl = `${BASE_URL}${API.GET_ADDRESS_AUTOCOMPLETE}?partialAddress=${partialAddress}`;
  const response = await fetch(requestUrl, { credentials: 'include' });
  if (!response.ok) {
    throw new Error('Failed get address autocomplete response');
  }
  return await response.json();
}

export function useAddressAutocomplete({ initialAddress = '' }: { initialAddress?: string }) {
  const [partialAddress, setPartialAddress] = useState(initialAddress);
  const [partialAddressDebounced, setPartialAddressDebounced] = useState(initialAddress);
  const setAddressAutocompleteDebounced = useDebouncedCallback(setPartialAddressDebounced, 300);

  function onSetAddress(address: string) {
    setPartialAddress(address);
    setAddressAutocompleteDebounced(address);
  }

  const queryHook = useQuery({
    queryKey: ['getAddressAutocomplete', partialAddressDebounced.trim()],
    queryFn: () => getAddressAutocomplete(partialAddressDebounced.trim()),
    enabled: partialAddressDebounced.trim().length >= MIN_AUTOCOMPLETE_ADDRESS_LENGTH,
  });

  return [partialAddress, onSetAddress, queryHook] as const;
}
